import type { CSSProperties } from 'react'
import type { MessageLadder } from '../../lib/api'
import { StageTrack, Status, Toggletip, type Stage } from '../kit'
import { ChannelIcon } from './ChannelIcon'
import { channelName, dayMonth, dayMonthTime, ladderStates, ladderWord, rungWord, tipText } from './journey'
import '../../styles/home.css'

// One reminder climbing its channels: email, then WhatsApp, then SMS, in the
// order the tenant's policy and the contact's preference put them.
//
// # WHAT IS DRAWN, AND WHAT IS NOT
//
// Every rung the ladder has tried, in order, with what the carrier said became
// of it underneath: delivered, read, not delivered, skipped with a reason. The
// read this is drawn from returns the rungs that were queued, settled or
// skipped and nothing about channels still to come, so a running ladder ends at
// the rung in flight. Drawing the rest from the policy would be a guess the
// ladder itself does not have to follow, and a reader chasing a vendor would
// take the guess as a plan.
//
// Under a running ladder a time line puts each try against the calendar with
// "now" at its end, so the gap since the last try reads at a glance.

function kindName(kind: string): string {
  switch (kind) {
    case 'vendor.reminder':
      return 'Reminder'
    case 'vendor.invite':
      return 'Invitation'
    case 'vendor.clarification':
      return 'Question about an answer'
    default:
      return 'Message'
  }
}

export function ReminderLadder({ ladder, now = new Date() }: { ladder: MessageLadder; now?: Date }) {
  const rungs = [...ladder.rungs].sort((a, b) => a.rungNo - b.rungNo)
  const states = ladderStates(ladder)
  const stages: Stage[] = rungs.map((r, i) => ({ key: String(r.rungNo), label: channelName(r.channel), state: states[i] ?? 'done' }))
  const word = ladderWord(ladder.state)
  const cols = { gridTemplateColumns: 'repeat(' + Math.max(1, rungs.length) + ', minmax(0, 1fr))' } as CSSProperties
  const running = ladder.state === 'RUNNING'

  return (
    <div className="as-ladder">
      <div className="as-ladder-head">
        <span className="as-ladder-title">{kindName(ladder.messageKind)}</span>
        <Status tone={word.tone}>{word.text}</Status>
        {ladder.outcome && <Toggletip label="What happened to this reminder">{tipText(ladder.outcome)}</Toggletip>}
        <span className="as-ladder-when kit-mono">
          {running || !ladder.finishedAt ? 'Started ' + dayMonth(ladder.startedAt) : 'Ended ' + dayMonth(ladder.finishedAt)}
        </span>
      </div>
      {rungs.length === 0 ? (
        <p className="as-line muted">No channel tried yet</p>
      ) : (
        <>
          <StageTrack stages={stages} label={kindName(ladder.messageKind) + ' channels, ' + rungs.length + ' tried'} />
          <ul className="as-receipts" style={cols}>
            {rungs.map((r) => {
              const w = rungWord(r.outcome)
              return (
                <li key={r.rungNo} className="as-receipt">
                  <span className="as-receipt-top">
                    <ChannelIcon channel={r.channel} size={14} />
                    <Status tone={w.tone}>{w.text}</Status>
                    {r.detail && <Toggletip label={'About the ' + channelName(r.channel) + ' try'}>{tipText(r.detail)}</Toggletip>}
                  </span>
                  {r.at && <span className="kit-mono as-muted">{dayMonthTime(r.at)}</span>}
                </li>
              )
            })}
          </ul>
          {running && <NowLine startedAt={ladder.startedAt} times={rungs.map((r) => r.at)} now={now} />}
        </>
      )}
    </div>
  )
}

// The tries against the calendar from the ladder's start to now. Labels closer
// than a fifth of the width to the one before are left off so dates never
// overprint; the receipts above carry every time in full.
function NowLine({ startedAt, times, now }: { startedAt: string; times: (string | undefined)[]; now: Date }) {
  const start = new Date(startedAt).getTime()
  const end = now.getTime()
  const span = end - start
  if (!Number.isFinite(span) || span <= 0) return null
  const at = (iso: string) => Math.max(0, Math.min(100, ((new Date(iso).getTime() - start) / span) * 100))
  let lastLabel = -100
  const ticks = times.flatMap((t, i) => {
    if (!t || Number.isNaN(new Date(t).getTime())) return []
    const pos = at(t)
    const labelled = pos - lastLabel >= 20 && pos <= 80
    if (labelled) lastLabel = pos
    return [{ key: i, pos, label: labelled ? dayMonth(t) : '' }]
  })
  return (
    <div className="as-nowline" aria-hidden="true">
      <span className="as-nowline-axis" />
      {ticks.map((t) => (
        <span key={t.key} className="as-nowline-tick" style={{ left: t.pos + '%' }}>
          {t.label && <span className="as-nowline-label">{t.label}</span>}
        </span>
      ))}
      <span className="as-nowline-now" style={{ left: '100%' }}>
        <span>now</span>
      </span>
    </div>
  )
}
